export type OutboundErrorCode =
  | "invalid-origin"
  | "cors-blocked-or-network-error"
  | "network-error"
  | "timeout"
  | "opaque-response"
  | "upstream-http-error";

export type OutboundProbeResult =
  | {
      ok: true;
      status: number;
      responseType: ResponseType;
      contentType: string | null;
      elapsedMs: number;
    }
  | {
      ok: false;
      code: OutboundErrorCode;
      message: string;
      elapsedMs: number;
    };

export async function probeOutbound(
  target: string,
  allowedOrigins: readonly string[],
  mode: RequestMode = "cors",
  timeoutMs = 8_000,
): Promise<OutboundProbeResult> {
  const started = performance.now();
  let url: URL;

  try {
    url = new URL(target);
  } catch {
    return failure("invalid-origin", "Target must be an absolute URL.", started);
  }

  if (!allowedOrigins.includes(url.origin)) {
    return failure("invalid-origin", "Target origin is not in the explicit client allowlist.", started);
  }

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method: "GET",
      mode,
      redirect: "error",
      signal: controller.signal,
      cache: "no-store",
    });

    const elapsedMs = Math.round(performance.now() - started);
    if (response.type === "opaque") {
      return { ok: false, code: "opaque-response", message: "Browser returned an opaque response; its body cannot be read.", elapsedMs };
    }
    if (!response.ok) {
      return { ok: false, code: "upstream-http-error", message: `Upstream returned HTTP ${response.status}.`, elapsedMs };
    }
    return {
      ok: true,
      status: response.status,
      responseType: response.type,
      contentType: response.headers.get("content-type"),
      elapsedMs,
    };
  } catch (error) {
    const elapsedMs = Math.round(performance.now() - started);
    if (error instanceof DOMException && error.name === "AbortError") {
      return { ok: false, code: "timeout", message: `Request exceeded ${timeoutMs} ms.`, elapsedMs };
    }
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return { ok: false, code: "network-error", message: "Browser reports that it is offline.", elapsedMs };
    }
    return {
      ok: false,
      code: "cors-blocked-or-network-error",
      message: "Browser rejected the cross-origin read; CORS and network failures are intentionally not guessed apart.",
      elapsedMs,
    };
  } finally {
    window.clearTimeout(timer);
  }
}

function failure(code: OutboundErrorCode, message: string, started: number): OutboundProbeResult {
  return { ok: false, code, message, elapsedMs: Math.round(performance.now() - started) };
}
